import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Modal, Typography, Row, Col, Divider, Grid } from "antd";
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const formatKey = (key) => {
    return key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const DetailRow = ({ label, value }) => (
    <Col xs={24} md={12}>
        <Text strong>{label}:</Text>
        <Text style={{ marginLeft: 8 }}>{String(value)}</Text>
    </Col>
);

const DetailSection = ({ title, children }) => (
    <>
        <Divider style={{ borderColor: "gray", marginTop: 0 }}>{title}</Divider>
        <Row gutter={[16, 16]}>{children}</Row>
    </>
);

const TransactionDetailsModal = ({ visible, onCancel, transactionDetails }) => {
    const { t } = useTranslation();
    const screens = useBreakpoint();
    const isMobile = screens.xs;

    const additionalDetails = useMemo(() => {
        if (!transactionDetails) return [];
        return Object.entries(transactionDetails)
            .filter(([key]) => key !== "user" && key !== "message")
            .map(([key, value]) => ({ key: formatKey(key), value }));
    }, [transactionDetails]);

    if (!transactionDetails) {
        return (
            <Modal
                title={<Title level={4}>{t('transaction_details')}</Title>}
                visible={visible}
                onCancel={onCancel}
                footer={null}
                width={isMobile ? "100%" : "720px"}
            >
                <Text>{t('no_transaction_details_available')}</Text>
            </Modal>
        );
    }

    return (
        <Modal
            title={<Title level={4} style={{ margin: 0 }}>{t('transaction_details')}</Title>}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width={isMobile ? "100%" : "720px"}
            bodyStyle={{
                height: isMobile ? "calc(100vh - 55px)" : "auto",
                overflowX: "hidden",
                padding: 0,
            }}
            style={
                isMobile
                    ? {
                        top: 0,
                        padding: 0,
                        margin: 0,
                        maxWidth: "100%",
                        height: "100vh",
                    }
                    : {}
            }
        >
            {transactionDetails.user && (
                <DetailSection title={t('user_details')}>
                    <DetailRow label={t('id')} value={transactionDetails.user.user_id} />
                    <DetailRow label={t('full_name')} value={transactionDetails.user.full_name} />
                </DetailSection>
            )}

            {transactionDetails.message && (
                <DetailSection title={t('message_details')}>
                    {Object.entries(transactionDetails.message).map(([key, value]) => (
                        <DetailRow key={key} label={formatKey(key)} value={value} />
                    ))}
                </DetailSection>
            )}

            <DetailSection title={t('additional_details')}>
                {additionalDetails.map(({ key, value }) => (
                    <DetailRow key={key} label={key} value={value} />
                ))}
            </DetailSection>
        </Modal>
    );
};

TransactionDetailsModal.propTypes = {
    visible: PropTypes.bool.isRequired,
    onCancel: PropTypes.func.isRequired,
    transactionDetails: PropTypes.shape({
        user: PropTypes.shape({
            user_id: PropTypes.string,
            full_name: PropTypes.string,
        }),
        message: PropTypes.object,
    }),
};

export default TransactionDetailsModal;
